import { useOrdersConfig } from '../config';
import { DETAIL_CHARGE_ROWS, DETAIL_SECTION_HEADING_STYLES, DETAIL_TOTAL_ROW } from '../consts';
import CopyButton from './CopyButton';
import InfoGrid from './InfoGrid';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'api/requests/core/users';
import { getDgcOrder } from 'api/requests/dgc/dgcOrders';
import { CrossIcon } from 'assets/svg';
import { Fragment } from 'react';
import Spinner2 from 'shared/components/Spinner2';
import { Role } from 'shared/consts/role';
import { queryKeyBuilder } from 'shared/query';
import { hasAnyOfRole } from 'shared/utils/auth';

type Props = {
  orderUuid: string | undefined;
  onModalClose: () => void;
};

function OrderDetails({ orderUuid, onModalClose }: Props) {
  const {
    data: order,
    isPending,
    isError,
  } = useQuery({
    queryKey: queryKeyBuilder.dgcOrderDetail(orderUuid ?? ''),
    queryFn: () => getDgcOrder(orderUuid!),
    enabled: !!orderUuid,
  });

  const { data: userData } = useQuery({
    queryKey: queryKeyBuilder.currentUser(),
    queryFn: getUserInfo,
  });
  const { features, generalInfoGroups, getStatusHint, CardsSection } = useOrdersConfig();
  const canResend = features.resend && hasAnyOfRole(userData, Role.GC_Edit);
  const statusHint = order ? getStatusHint(order) : null;

  return (
    <Transition appear show={!!orderUuid} as={Fragment}>
      <Dialog as="div" className="relative z-30 w-full h-full" onClose={onModalClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-palette-secondary transition-opacity opacity-20" />
        </Transition.Child>
        <div className="fixed inset-0">
          <div className="flex h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="max-w-full max-h-full w-full h-full pb-8 px-6 gap-6 md:w-[44rem] md:h-auto md:max-h-[85vh] md:pb-10 md:px-10 md:gap-6 md:rounded-xl text-palette-secondary overflow-auto scrollbar flex flex-col bg-white shadow-md">
                {isPending ? (
                  <div className="w-full grow flex flex-col items-center justify-center">
                    <Spinner2 />
                  </div>
                ) : isError ? (
                  <div className="w-full grow flex flex-col items-center justify-center">
                    <div className="mt-6 max-w-[360px] text-center font-klarheit text-3xl">Something went wrong</div>
                    <div className="max-w-[360px] text-center text-base mt-4">Try again later</div>
                    <button
                      className="w-52 justify-center text-center group transition-colors hover:text-palette-gold text-white bg-palette-burgundy flex px-6 py-3 gap-3 items-center rounded-full mt-14"
                      onClick={onModalClose}
                    >
                      <span className="tracking-tight text-base text-center">Close</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="group sticky top-0 z-10 flex gap-3 items-center border-b border-palette-color-bg-grey bg-white pt-8 pb-3 md:pt-10">
                      <span className="font-klarheit tracking-tight text-3xl">
                        {order?.order_ref ?? 'Order Details'}
                      </span>
                      {order?.order_ref && <CopyButton value={order.order_ref} label="Order Ref" />}
                      {order && (
                        <span className="rounded-full bg-palette-sbiu-grey px-3 py-1 text-sm tracking-wide">
                          {order.status}
                        </span>
                      )}
                      <div className="grow" />
                      <button className="flex" aria-label="Close order details" onClick={onModalClose}>
                        <CrossIcon className="w-[18px] h-[18px]" />
                      </button>
                    </div>
                    {statusHint && (
                      <div className="bg-palette-sbiu-grey px-4 py-4 text-base md:px-10" role="status">
                        {statusHint}
                      </div>
                    )}
                    <div className="flex flex-col divide-y divide-palette-color-bg-grey">
                      {generalInfoGroups.map((group) => (
                        <section key={group.title} className="flex flex-col gap-2 py-3 first:pt-0">
                          <h4 className={DETAIL_SECTION_HEADING_STYLES}>{group.title}</h4>
                          <InfoGrid rows={group.rows} entity={order} columns={group.columns} />
                        </section>
                      ))}
                      <section className="flex flex-col gap-2 py-3">
                        <h4 className={DETAIL_SECTION_HEADING_STYLES}>Charges</h4>
                        <div className="flex flex-col gap-2">
                          {DETAIL_CHARGE_ROWS.map((cellSchema) => (
                            <div key={cellSchema.headerLabel} className="flex justify-between gap-4 text-base">
                              <span className="text-palette-secondary-grey">{cellSchema.headerLabel}</span>
                              <span>{order ? cellSchema.getValue(order) : '—'}</span>
                            </div>
                          ))}
                          <div className="flex justify-between gap-4 border-t border-palette-color-bg-grey pt-2 text-base font-bold">
                            <span>{DETAIL_TOTAL_ROW.headerLabel}</span>
                            <span>{order ? DETAIL_TOTAL_ROW.getValue(order) : '—'}</span>
                          </div>
                        </div>
                      </section>
                      <section className="flex flex-col gap-1 py-4 last:pb-0">
                        <h4 className={DETAIL_SECTION_HEADING_STYLES}>Cards</h4>
                        {order && <CardsSection order={order} canResend={canResend} />}
                      </section>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default OrderDetails;
